/**
 * The gate in front of the pages that belong to whoever is signed in.
 *
 * `RequireStaff` and `RequireVolunteer` both ask "are you the right *kind* of
 * person for this screen". This one only asks whether you are signed in, which
 * is the whole question for a page about your own account: every role has one,
 * and there is no wrong door to arrive at.
 *
 * ---------------------------------------------------------------------------
 * Why it exists
 * ---------------------------------------------------------------------------
 * `/profile` was originally declared twice — once inside the volunteer block and
 * once inside the staff block — on the reasoning that the two guards differ only
 * in where they send somebody who does not belong, and that a page about your
 * own account has no such person.
 *
 * That reasoning was wrong, and wrong in a way that only shows up at runtime.
 * React Router matches the FIRST declaration, so every account reaching
 * `/profile` was handed to `RequireVolunteer`; a coordinator or an admin failed
 * its role check and was redirected to the dashboard. The second declaration
 * never ran. Two routes for one path is not "either guard applies" — it is
 * "whichever was written first applies to everybody".
 */

import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LoadingState } from '../components/common';
import { useAuth } from '../hooks/useAuth';

export function RequireUser({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  // Same three states as the other two guards. Rendering the sign-in page while
  // the stored token is still being checked flashes a login form at somebody who
  // is already signed in.
  if (loading) return <LoadingState label="Checking your session…" />;

  if (!user) {
    // No `state.from`. It used to carry the page being guarded, and the
    // sign-in page sent you back to it — which meant the next session
    // inherited the last one’s position. Signing in lands on `/`.
    return <Navigate to="/login" replace />;
  }

  // No role check, deliberately. There is no third state here — that is the
  // difference between this guard and the other two, not an omission.
  return <>{children}</>;
}
